import React, { useState, useEffect } from 'react';
import {
  Button,
  Card,
  Flex,
  Heading,
  Text,
  TextField,
  Alert,
  Loader,
  Badge,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Divider,
  View
} from '@aws-amplify/ui-react';
import { Auth } from 'aws-amplify';
import corsProxy from '../utils/corsProxy';

const ProfilePage = ({ onClose }) => {
  // User profile state
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Form state
  const [formData, setFormData] = useState({
    name: '',
    email: ''
  });
  const [updating, setUpdating] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);

  // Projects state
  const [userProjects, setUserProjects] = useState([]);
  const [loadingProjects, setLoadingProjects] = useState(true);

  // Confirmation dialog state
  const [confirmDialog, setConfirmDialog] = useState({
    isOpen: false,
    title: '',
    message: '',
    confirmText: 'Confirm',
    onConfirm: null,
    variant: 'destructive'
  });

  useEffect(() => {
    fetchUserProfile();
    fetchUserProjects();
  }, []);

  useEffect(() => {
    if (user) {
      const hasNameChange = formData.name !== (user.name || '');
      const hasEmailChange = formData.email !== (user.email || '');
      setHasChanges(hasNameChange || hasEmailChange);
    }
  }, [formData, user]);

  const fetchUserProfile = async () => {
    try {
      setLoading(true);
      setError('');
      
      // Get current authenticated user from Cognito
      const currentUser = await Auth.currentAuthenticatedUser();
      const userAttributes = currentUser.attributes;
      
      const userData = {
        id: currentUser.username,
        email: userAttributes.email,
        name: userAttributes.name || userAttributes.given_name || '',
        sub: userAttributes.sub,
        emailVerified: userAttributes.email_verified === 'true'
      };

      setUser(userData);
      setFormData({
        name: userData.name,
        email: userData.email
      });

      console.log('User profile loaded:', userData);
    } catch (error) {
      console.error('Error fetching user profile:', error);
      setError('Failed to load user profile. Please try refreshing the page.');
    } finally {
      setLoading(false);
    }
  };

  const fetchUserProjects = async () => {
    try {
      setLoadingProjects(true);
      setError('');
      
      console.log('Fetching user projects...');
      
      // Use the user profile API endpoint
      const response = await corsProxy.get('/user/projects');
      
      console.log('User projects response:', response);
      
      if (response && response.projects) {
        setUserProjects(response.projects);
      } else {
        setUserProjects([]);
      }
      
    } catch (error) {
      console.error('Error fetching user projects:', error);
      
      // Set error message but don't show empty state
      setError('Unable to load projects at this time. Please try refreshing the page.');
      setUserProjects([]);
      
    } finally {
      setLoadingProjects(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    setError('');
    setSuccess('');
  };

  const handleSaveProfile = async () => {
    if (!hasChanges) return;

    setConfirmDialog({
      isOpen: true,
      title: 'Update Profile',
      message: 'Are you sure you want to update your profile information? This will change how your name and email appear across all projects.',
      confirmText: 'Update Profile',
      onConfirm: async () => {
        try {
          setUpdating(true);
          setError('');
          setSuccess('');

          const currentUser = await Auth.currentAuthenticatedUser();
          
          // Update user attributes in Cognito
          const attributesToUpdate = {};
          
          if (formData.name !== user.name) {
            attributesToUpdate.name = formData.name;
          }
          
          if (formData.email !== user.email) {
            attributesToUpdate.email = formData.email;
          }

          if (Object.keys(attributesToUpdate).length > 0) {
            await Auth.updateUserAttributes(currentUser, attributesToUpdate);
            
            // If email changed, user will need to verify the new email
            if (attributesToUpdate.email) {
              setSuccess('Profile updated successfully! Please check your new email for a verification link.');
            } else {
              setSuccess('Profile updated successfully!');
            }

            // Update local user state
            setUser(prev => ({
              ...prev,
              ...attributesToUpdate
            }));

            // Reset hasChanges flag
            setHasChanges(false);
          }

        } catch (error) {
          console.error('Error updating profile:', error);
          setError('Failed to update profile: ' + error.message);
        } finally {
          setUpdating(false);
        }
      },
      variant: 'primary'
    });
  };

  const handleLeaveProject = async (project) => {
    setConfirmDialog({
      isOpen: true,
      title: 'Leave Project',
      message: `Are you sure you want to leave "${project.name}"? This action cannot be undone and you will lose your selected number (${project.selectedNumber || 'none selected'}) if you have one.`,
      confirmText: 'Leave Project',
      onConfirm: async () => {
        try {
          console.log('Leaving project:', project.projectId);
          
          // Call API to remove user from project
          await corsProxy.delete(`/projects/${project.projectId}/participants/${user.id}`);
          
          // Remove project from local state
          setUserProjects(prev => prev.filter(p => p.projectId !== project.projectId));
          
          setSuccess(`Successfully left project "${project.name}"`);
          
        } catch (error) {
          console.error('Error leaving project:', error);
          setError('Failed to leave project: ' + error.message);
        }
      },
      variant: 'destructive'
    });
  };

  const resetForm = () => {
    if (user) {
      setFormData({
        name: user.name,
        email: user.email
      });
      setError('');
      setSuccess('');
    }
  };

  const getStatusBadgeVariation = (status) => {
    switch (status?.toLowerCase()) {
      case 'active': return 'success';
      case 'setup': return 'info';
      case 'completed': return 'warning';
      case 'paused': return 'error';
      default: return 'info';
    }
  };

  const getRoleBadgeVariation = (role) => {
    switch (role?.toLowerCase()) {
      case 'admin': return 'error';
      case 'participant': return 'success';
      default: return 'info';
    }
  };

  if (loading) {
    return (
      <Card variation="elevated" padding="1.5rem">
        <Flex justifyContent="center" alignItems="center" minHeight="200px">
          <Loader size="large" />
          <Text marginLeft="1rem">Loading your profile...</Text>
        </Flex>
      </Card>
    );
  }

  return (
    <Card variation="elevated" padding="1.5rem">
      <Flex justifyContent="space-between" alignItems="center" marginBottom="1.5rem">
        <Heading level={3}>Profile Settings</Heading>
        {onClose && (
          <Button variation="link" onClick={onClose}>
            ✕ Close
          </Button>
        )}
      </Flex>

      {error && (
        <Alert variation="error" marginBottom="1rem" isDismissible onDismiss={() => setError('')}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert variation="success" marginBottom="1rem" isDismissible onDismiss={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      {/* Profile Information Section */}
      <Card variation="outlined" padding="1rem" marginBottom="1.5rem">
        <Heading level={4} marginBottom="1rem">Personal Information</Heading>
        
        <Flex direction="column" gap="1rem">
          <TextField
            label="Full Name"
            value={formData.name}
            onChange={(e) => handleInputChange('name', e.target.value)}
            placeholder="Enter your full name"
            isRequired
          />
          
          <TextField
            label="Email Address"
            type="email"
            value={formData.email}
            onChange={(e) => handleInputChange('email', e.target.value)}
            placeholder="Enter your email address"
            isRequired
          />
          
          <Text fontSize="0.9rem" color="gray">
            Note: If you change your email address, you'll need to verify the new email before it becomes active.
          </Text>
        </Flex>

        <Flex gap="1rem" marginTop="1.5rem">
          <Button
            variation="primary"
            onClick={handleSaveProfile}
            isDisabled={!hasChanges || updating}
            isLoading={updating}
            loadingText="Updating..."
          >
            Save Changes
          </Button>
          
          <Button
            onClick={resetForm}
            isDisabled={!hasChanges || updating}
          >
            Reset
          </Button>
        </Flex>
      </Card>

      {/* Projects Section */}
      <Card variation="outlined" padding="1rem">
        <Heading level={4} marginBottom="1rem">My Projects</Heading>
        
        {loadingProjects ? (
          <Flex justifyContent="center" padding="2rem">
            <Loader />
            <Text marginLeft="1rem">Loading your projects...</Text>
          </Flex>
        ) : userProjects.length > 0 ? (
          <Table variation="striped">
            <TableHead>
              <TableRow>
                <TableCell as="th">Project Name</TableCell>
                <TableCell as="th">Status</TableCell>
                <TableCell as="th">Role</TableCell>
                <TableCell as="th">Position</TableCell>
                <TableCell as="th">Selected Number</TableCell>
                <TableCell as="th">Progress</TableCell>
                <TableCell as="th">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {userProjects.map((project) => (
                <TableRow key={project.projectId}>
                  <TableCell>
                    <Text fontWeight="bold">{project.name}</Text>
                    <Text fontSize="0.8rem" color="gray">
                      Range: {project.numberRange?.min}-{project.numberRange?.max}
                    </Text>
                  </TableCell>
                  <TableCell>
                    <Badge variation={getStatusBadgeVariation(project.status)}>
                      {project.status}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <Badge variation={getRoleBadgeVariation(project.role)}>
                      {project.role}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    {project.selectionOrder ? (
                      <Badge variation="info">#{project.selectionOrder}</Badge>
                    ) : (
                      <Text color="gray">TBD</Text>
                    )}
                  </TableCell>
                  <TableCell>
                    {project.selectedNumber ? (
                      <Badge variation="success">#{project.selectedNumber}</Badge>
                    ) : (
                      <Text color="gray">Not selected</Text>
                    )}
                  </TableCell>
                  <TableCell>
                    <Text fontSize="0.8rem">
                      {project.selectedCount || 0} / {project.totalParticipants || 0} selected
                    </Text>
                  </TableCell>
                  <TableCell>
                    <Button
                      size="small"
                      variation="destructive"
                      onClick={() => handleLeaveProject(project)}
                    >
                      Leave Project
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        ) : (
          <View textAlign="center" padding="2rem">
            <Text color="gray" fontSize="1.1rem">
              You are not currently participating in any projects.
            </Text>
            <Text color="gray" fontSize="0.9rem" marginTop="0.5rem">
              When you're added to a project, it will appear here.
            </Text>
          </View>
        )}
      </Card>

      {/* Confirmation Dialog */}
      {confirmDialog.isOpen && (
        <View
          position="fixed"
          top="0"
          left="0"
          right="0"
          bottom="0"
          backgroundColor="rgba(0, 0, 0, 0.5)"
          display="flex"
          justifyContent="center"
          alignItems="center"
          style={{ zIndex: 1000 }}
        >
          <Card
            variation="elevated"
            padding="1.5rem"
            maxWidth="500px"
            width="90%"
          >
            <Heading level={4} marginBottom="1rem">{confirmDialog.title}</Heading>
            <Text marginBottom="1.5rem">{confirmDialog.message}</Text>
            <Flex gap="1rem" justifyContent="flex-end">
              <Button
                onClick={() => setConfirmDialog({ ...confirmDialog, isOpen: false })}
              >
                Cancel
              </Button>
              <Button
                variation={confirmDialog.variant}
                onClick={() => {
                  setConfirmDialog({ ...confirmDialog, isOpen: false });
                  if (confirmDialog.onConfirm) {
                    confirmDialog.onConfirm();
                  }
                }}
              >
                {confirmDialog.confirmText}
              </Button>
            </Flex>
          </Card>
        </View>
      )}
    </Card>
  );
};

export default ProfilePage;
